import { supabase } from '../../../shared/lib/supabase';

// Define the Tournament interface based on your schema
export interface Tournament {
  id: string;
  name: string;
  logo: string | null;
  country: string;
  created_at: string;
  updated_at: string;
  active: boolean;
  organisation_id: string | null;
  data_source: string;
  organisations?: {
    name: string;
  };
}

export interface TournamentCreateInput {
  name: string;
  logo?: string | null;
  country: string;
  active?: boolean;
  organisation_id?: string | null;
  data_source: string;
}

export interface TournamentUpdateInput {
  name?: string;
  logo?: string | null;
  country?: string;
  active?: boolean;
  organisation_id?: string | null;
  data_source?: string;
}

class TournamentsService {
  async getAll(): Promise<Tournament[]> {
    try {
      console.log('🔍 Fetching tournaments...');
      const { data, error } = await supabase
        .from('tournaments')
        .select('*, organisations(name)')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching tournaments:', error);
        throw error;
      }

      console.log('✅ Tournaments fetched successfully:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('Error in tournamentsService.getAll:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<Tournament | null> {
    try {
      const { data, error } = await supabase
        .from('tournaments')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching tournament:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in tournamentsService.getById:', error);
      throw error;
    }
  }

  async create(tournament: TournamentCreateInput): Promise<Tournament> {
    try {
      console.log('🔍 Creating tournament:', tournament);
      const { data, error } = await supabase
        .from('tournaments')
        .insert([tournament])
        .select()
        .single();

      if (error) {
        console.error('Error creating tournament:', error);
        throw error;
      }

      console.log('✅ Tournament created successfully:', data);
      return data;
    } catch (error) {
      console.error('Error in tournamentsService.create:', error);
      throw error;
    }
  }

  async update(id: string, tournament: TournamentUpdateInput): Promise<Tournament> {
    try {
      console.log('🔍 Updating tournament:', id, tournament);
      const { data, error } = await supabase
        .from('tournaments')
        .update(tournament)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating tournament:', error);
        throw error;
      }

      console.log('✅ Tournament updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Error in tournamentsService.update:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      console.log('🔍 Deleting tournament:', id);
      const { error } = await supabase
        .from('tournaments')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting tournament:', error);
        throw error;
      }

      console.log('✅ Tournament deleted successfully');
    } catch (error) {
      console.error('Error in tournamentsService.delete:', error);
      throw error;
    }
  }
}

export const tournamentsService = new TournamentsService();
